import { Timestamp } from 'firebase/firestore';

/**
 * Task sort order options
 */
export type TaskSortOrder = 
  | 'active-first'      // Active tasks first, then completed (default)
  | 'completed-first'   // Completed tasks first, then active
  | 'alphabetical'      // Sort by title A-Z
  | 'custom-priority';  // Sort by priority (high → low)

/**
 * Hide completed tasks after duration
 */
export type HideCompletedAfter = 
  | '1-day'
  | '3-days'
  | '1-week'
  | '2-weeks'    // default
  | '1-month'
  | 'never';

/**
 * Behavior when task is marked complete
 */
export type OnCompletionBehavior = 
  | 'move-to-bottom'    // Move to bottom of list (default)
  | 'stay-in-place'     // Keep in current position
  | 'move-to-archived'; // Move to archived section

/**
 * Task Preferences
 * Stored at: /users/{uid}/private/taskPreferences
 */
export interface TaskPreferences {
  /** Sort order for tasks */
  sortOrder: TaskSortOrder;
  
  /** Hide completed tasks after this duration */
  hideCompletedAfter: HideCompletedAfter;
  
  /** Behavior when marking task complete */
  onCompletion: OnCompletionBehavior;
  
  /** Show archived tasks in the list */
  showArchivedTasks: boolean;
  
  /** Enable celebration confetti when completing last task */
  enableCelebrations: boolean;
  
  /** Show empty state message when no open tasks */
  showEmptyState: boolean;
  
  /** Last updated timestamp */
  updatedAt?: Timestamp;
}

/**
 * Default task preferences
 */
export const defaultTaskPreferences: TaskPreferences = {
  sortOrder: 'active-first',
  hideCompletedAfter: '2-weeks',
  onCompletion: 'move-to-bottom',
  showArchivedTasks: false,
  enableCelebrations: true,
  showEmptyState: true,
};

/**
 * Convert HideCompletedAfter to milliseconds
 */
export function hideCompletedAfterToMs(duration: HideCompletedAfter): number | null {
  switch (duration) {
    case '1-day':
      return 24 * 60 * 60 * 1000;
    case '3-days':
      return 3 * 24 * 60 * 60 * 1000;
    case '1-week':
      return 7 * 24 * 60 * 60 * 1000;
    case '2-weeks':
      return 14 * 24 * 60 * 60 * 1000;
    case '1-month':
      return 30 * 24 * 60 * 60 * 1000;
    case 'never':
      return null;
  }
}

/**
 * Check if a task should be hidden based on completion time and preferences
 */
export function shouldHideCompletedTask(
  completedAt: Timestamp | Date | null | undefined,
  hideAfter: HideCompletedAfter
): boolean {
  if (!completedAt || hideAfter === 'never') {
    return false;
  }

  const hideAfterMs = hideCompletedAfterToMs(hideAfter);
  if (hideAfterMs === null) {
    return false;
  }

  const completedTime = completedAt instanceof Date 
    ? completedAt.getTime() 
    : completedAt.toMillis();
  
  const now = Date.now();
  const elapsed = now - completedTime;

  return elapsed > hideAfterMs;
}

/**
 * Human-readable labels for preferences
 */
export const taskPreferenceLabels = {
  sortOrder: {
    'active-first': 'Active first',
    'completed-first': 'Completed first',
    'alphabetical': 'Alphabetical (A-Z)',
    'custom-priority': 'Custom priority',
  },
  hideCompletedAfter: {
    '1-day': '1 day',
    '3-days': '3 days',
    '1-week': '1 week',
    '2-weeks': '2 weeks',
    '1-month': '1 month',
    'never': 'Never',
  },
  onCompletion: {
    'move-to-bottom': 'Move to bottom',
    'stay-in-place': 'Stay in place',
    'move-to-archived': 'Move to archived',
  },
};
